import { useEffect } from "react";

/**
 * Lightweight SEO hook — sets <title>, meta description, canonical, robots
 * and a few OG tags per-route without adding a heavy helmet dependency.
 *
 * Usage:
 *   useSeo({
 *     title: "...",
 *     description: "...",
 *     canonical: "https://www.invitationwala.online/careers",
 *     noindex: false,
 *   });
 */
export default function useSeo({
  title,
  description,
  canonical,
  ogImage,
  noindex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content);
    };

    if (description) {
      upsertMeta('meta[name="description"]', {
        name: "description",
        content: description,
      });
      upsertMeta('meta[property="og:description"]', {
        property: "og:description",
        content: description,
      });
      upsertMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: description,
      });
    }

    if (title) {
      upsertMeta('meta[property="og:title"]', {
        property: "og:title",
        content: title,
      });
      upsertMeta('meta[name="twitter:title"]', {
        name: "twitter:title",
        content: title,
      });
    }

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);

      upsertMeta('meta[property="og:url"]', {
        property: "og:url",
        content: canonical,
      });
    }

    if (ogImage) {
      upsertMeta('meta[property="og:image"]', {
        property: "og:image",
        content: ogImage,
      });
      upsertMeta('meta[name="twitter:image"]', {
        name: "twitter:image",
        content: ogImage,
      });
    }

    // Robots
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    });
  }, [title, description, canonical, ogImage, noindex]);
}
